import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ShieldCheck, ShieldAlert, Clock, Loader2, Users, KeyRound, Save,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const ROLE_LABEL = {
  owner: "Dirigeant", admin: "Admin", cfo: "DAF",
  accountant: "Comptable interne", external_accountant: "Expert-comptable",
  manager: "Manager", hr: "RH", user: "Utilisateur", viewer: "Consultation",
};

const STATUS_STYLE = {
  enabled: { color: "border-emerald-200 bg-emerald-50 text-emerald-700",
             label: "2FA active", icon: ShieldCheck },
  grace_active: { color: "border-amber-200 bg-amber-50 text-amber-800",
                  label: "En grâce", icon: Clock },
  grace_expired: { color: "border-red-200 bg-red-50 text-red-800",
                   label: "Grâce expirée", icon: ShieldAlert },
  optional_disabled: { color: "border-slate-200 bg-slate-50 text-slate-600",
                       label: "Optionnelle — non activée", icon: KeyRound },
};

const OPTIONAL_ROLES = ["manager", "hr", "external_accountant", "user", "viewer"];

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR"); }
  catch { return iso; }
}

export default function TenantSecurity() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("tenant.update");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [extra, setExtra] = useState([]); // pending edit state

  const load = async () => {
    try {
      const r = await api.get("/tenant/security/members");
      setData(r.data);
      setExtra(r.data.extra_required_roles || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleRole = (role) => {
    setExtra(prev => prev.includes(role)
      ? prev.filter(r => r !== role)
      : [...prev, role]);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/tenant/security/required-roles",
        { extra_roles: extra });
      toast.success("Politique 2FA mise à jour.");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
    </div>
  );
  if (!data) return null;

  const members = data.members || [];
  const totals = {
    enabled: members.filter(m => m.status === "enabled").length,
    grace_active: members.filter(m => m.status === "grace_active").length,
    grace_expired: members.filter(m => m.status === "grace_expired").length,
    total: members.length,
  };
  const dirty = JSON.stringify(extra.slice().sort()) !==
                JSON.stringify((data.extra_required_roles || []).slice().sort());

  return (
    <div className="space-y-6" data-testid="tenant-security-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
          Sécurité tenant
        </div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          Politique 2FA de l&apos;équipe
        </h1>
        <p className="mt-1 text-sm text-slate-500 max-w-2xl">
          Vue admin sur l&apos;état d&apos;enrôlement de chaque membre. Les rôles
          <strong> owner, admin, DAF, comptable interne</strong> ont la 2FA obligatoire
          par défaut — vous pouvez étendre cette obligation aux autres rôles (jamais
          la réduire en dessous du socle).
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4" data-testid="sec-kpi-enabled">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Activée</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums text-emerald-700">
            {totals.enabled}
          </div>
        </Card>
        <Card className="p-4" data-testid="sec-kpi-grace">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">En grâce</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums text-amber-700">
            {totals.grace_active}
          </div>
        </Card>
        <Card className="p-4" data-testid="sec-kpi-expired">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Grâce expirée</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums text-red-700">
            {totals.grace_expired}
          </div>
        </Card>
        <Card className="p-4 bg-slate-50" data-testid="sec-kpi-total">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Membres</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
            {totals.total}
          </div>
        </Card>
      </div>

      {/* Policy card */}
      <Card className="p-6 space-y-4" data-testid="sec-policy-card">
        <div>
          <div className="font-medium text-slate-900">Rôles concernés par la 2FA obligatoire</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Socle non modifiable : <strong>{(data.hard_required_roles || []).map(r => ROLE_LABEL[r] || r).join(", ")}</strong>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
          {OPTIONAL_ROLES.map(role => {
            const on = extra.includes(role);
            return (
              <label key={role}
                     className={cn(
                       "flex items-center justify-between gap-3 p-3 rounded-md border cursor-pointer",
                       on ? "border-blue-300 bg-blue-50/50" : "border-slate-200",
                       !canEdit && "opacity-60 cursor-not-allowed"
                     )}>
                <div>
                  <div className="font-medium text-sm text-slate-900">
                    {ROLE_LABEL[role] || role}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {on
                      ? "Obligatoire — grâce 7 j pour s'enrôler"
                      : "Optionnelle — chaque utilisateur choisit"}
                  </div>
                </div>
                <Switch checked={on}
                        onCheckedChange={() => canEdit && toggleRole(role)}
                        disabled={!canEdit}
                        data-testid={`role-toggle-${role}`} />
              </label>
            );
          })}
        </div>
        {canEdit && (
          <div className="flex justify-end pt-2 border-t border-slate-100">
            <Button onClick={save} disabled={saving || !dirty}
                    data-testid="sec-save-policy-btn">
              {saving
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <Save className="h-4 w-4 mr-1.5" strokeWidth={2} />}
              Enregistrer la politique
            </Button>
          </div>
        )}
      </Card>

      {/* Members table */}
      <Card className="overflow-hidden" data-testid="sec-members-card">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 font-medium text-slate-900">
            <Users className="h-4 w-4 text-slate-500" strokeWidth={2} />
            État par membre
          </div>
          <div className="text-xs text-slate-500">
            Non-résolus en tête.
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Membre</TableHead>
              <TableHead>Rôle</TableHead>
              <TableHead>Statut 2FA</TableHead>
              <TableHead>Détail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map(m => {
              const s = STATUS_STYLE[m.status] || STATUS_STYLE.optional_disabled;
              const Icon = s.icon;
              return (
                <TableRow key={m.user_id} data-testid={`member-row-${m.user_id}`}>
                  <TableCell>
                    <div className="text-sm font-medium">{m.full_name || m.email}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{m.email}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">
                    {ROLE_LABEL[m.role] || m.role}
                    {m.required && (
                      <div className="text-[10px] text-red-600 mt-0.5">2FA obligatoire</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("text-[10px]", s.color)}>
                      <Icon className="h-3 w-3 mr-1" strokeWidth={2} />
                      {s.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {m.status === "enabled"
                      ? `Activée le ${fmtDateTime(m.enrolled_at)}`
                      : m.status === "grace_active"
                        ? `Grâce jusqu'au ${fmtDateTime(m.grace_deadline)}`
                        : m.status === "grace_expired"
                          ? `Grâce expirée le ${fmtDateTime(m.grace_deadline)} — session restreinte au prochain login`
                          : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
